// Frontend/src/api/invitations.ts
import api from "./client";

/**
 * Invitation Types
 */
export type InvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export interface ResidentInvitation {
  id: string;
  resident: string;
  email: string;
  invitation_code: string;
  status: InvitationStatus;
  sent_at: string;
  expires_at: string;
  accepted_at: string | null;
  sent_by: string;
  password_changed: boolean;
  resident_name: string;
  resident_unit: string;
  sent_by_name: string;
  is_expired: boolean;
  is_valid: boolean;
}

export interface BulkInviteRequest {
  resident_ids: string[];
  message?: string;
  expiry_days?: number;
}

export interface BulkInviteResponse {
  success: boolean;
  invitations_sent: number;
  invitations: ResidentInvitation[];
  errors: Array<{
    resident_id: string;
    email: string;
    error: string;
  }>;
}

export interface InvitationStatusCheck {
  invitation_code: string;
  email: string;
}

export interface InvitationStatusResponse {
  valid: boolean;
  status: InvitationStatus;
  expired: boolean;
  expires_at: string;
  password_changed: boolean;
}

/**
 * OTP Types
 */
export type OTPPurpose = "login" | "password_reset" | "email_verify";

export interface OTPRequest {
  email: string;
  purpose: OTPPurpose;
}

export interface OTPRequestResponse {
  success: boolean;
  message: string;
  expires_in_minutes: number;
}

export interface OTPVerify {
  email: string;
  code: string;
  purpose: OTPPurpose;
}

export interface OTPVerifyResponse {
  success: boolean;
  access: string;
  refresh: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
  };
  requires_password_change: boolean;
}

/**
 * Password Change Types
 */
export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface PasswordChangeResponse {
  success: boolean;
  message: string;
}

export interface PasswordChangeStatusResponse {
  requires_password_change: boolean;
  temporary_password_set_at?: string;
  password_changed_at?: string;
}

/**
 * Invitation API Functions
 */

/**
 * Send bulk invitations to multiple residents
 */
export async function sendBulkInvitations(
  data: BulkInviteRequest
): Promise<BulkInviteResponse> {
  const res = await api.post<BulkInviteResponse>("/auth/invitations/bulk_invite/", data);
  return res.data;
}

/**
 * Resend an invitation
 */
export async function resendInvitation(id: string): Promise<{ success: boolean; message: string }> {
  const res = await api.post<{ success: boolean; message: string }>(
    `/auth/invitations/${id}/resend/`
  );
  return res.data;
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(id: string): Promise<{ success: boolean; message: string }> {
  const res = await api.post<{ success: boolean; message: string }>(
    `/auth/invitations/${id}/revoke/`
  );
  return res.data;
}

/**
 * Check invitation status (public endpoint)
 */
export async function checkInvitationStatus(
  data: InvitationStatusCheck
): Promise<InvitationStatusResponse> {
  const res = await api.post<InvitationStatusResponse>(
    "/auth/invitations/check_status/",
    data
  );
  return res.data;
}

/**
 * Get all invitations (for admin)
 */
export async function listInvitations(): Promise<ResidentInvitation[]> {
  const res = await api.get<ResidentInvitation[]>("/auth/invitations/");
  return res.data;
}

/**
 * OTP API Functions
 */

/**
 * Request OTP code
 */
export async function requestOTP(data: OTPRequest): Promise<OTPRequestResponse> {
  const res = await api.post<OTPRequestResponse>("/auth/otp/request_otp/", data);
  return res.data;
}

/**
 * Verify OTP code
 */
export async function verifyOTP(data: OTPVerify): Promise<OTPVerifyResponse> {
  const res = await api.post<OTPVerifyResponse>("/auth/otp/verify_otp/", data);
  return res.data;
}

/**
 * Password Change API Functions
 */

/**
 * Check if password change is required
 */
export async function checkPasswordChangeStatus(): Promise<PasswordChangeStatusResponse> {
  const res = await api.get<PasswordChangeStatusResponse>("/auth/password/status/");
  return res.data;
}

/**
 * Change password (first time)
 */
export async function changePassword(data: PasswordChangeRequest): Promise<PasswordChangeResponse> {
  const res = await api.post<PasswordChangeResponse>("/auth/password/change_password/", data);
  return res.data;
}
